import {
  DELTA_KEYS,
  type AssetClassRollup,
  type Bucket,
  type CurrencyExposure,
  type Delta,
  type Deltas,
  type GeographyRollup,
  type HoldingRow,
  type PnL,
  type PortfolioResponse,
  type RiskHeatmapPoint,
} from '../../shared/types';

export type Scope = 'liquid' | 'total';

type VirtualMeta = {
  bucket_id: string;
  bucket_name: string;
  bucket_accent: string;
  asset_class: string;
  region: string;
  risk: number;
};

const VIRTUAL_META: Record<string, VirtualMeta> = {
  real_estate: {
    bucket_id: 'real_estate',
    bucket_name: 'Real Estate',
    bucket_accent: 'var(--bucket-real-estate)',
    asset_class: 'real_estate',
    region: 'Europe',
    risk: 3,
  },
  artwork: {
    bucket_id: 'alternatives',
    bucket_name: 'Alternatives',
    bucket_accent: 'var(--bucket-alternatives)',
    asset_class: 'alternatives',
    region: 'Global',
    risk: 5,
  },
  private_equity: {
    bucket_id: 'private_equity',
    bucket_name: 'Private Equity',
    bucket_accent: 'var(--bucket-pe)',
    asset_class: 'private_equity',
    region: 'Global',
    risk: 5,
  },
};

const PRIVATE_ASSETS_CCY = 'EUR';

function zeroDeltas(): Deltas {
  const z = { abs_usd: 0, abs_eur: 0, pct: 0 };
  return { d1: z, d7: z, d30: z, d90: z, ytd: z, y1: z, y2: z };
}

type VirtualEntry = { bucket: Bucket; meta: VirtualMeta };

export function deriveForScope(
  data: PortfolioResponse,
  scope: Scope
): PortfolioResponse {
  if (scope === 'liquid') return data;

  const total = data.totals.grand_total_usd;
  const deltaBaseByWindow = portfolioBaseByWindow(total, data.totals.deltas);
  const rebasedTotalsDeltas = rebaseDeltasToPortfolio(data.totals.deltas, deltaBaseByWindow);
  const entries: VirtualEntry[] = [];
  let privateUsd = 0;

  for (const item of data.non_tradable) {
    const meta = VIRTUAL_META[item.id];
    if (!meta || item.value_usd == null || item.value_eur == null) continue;
    const row: HoldingRow = {
      symbol: meta.bucket_name,
      name: item.name,
      bucket_id: meta.bucket_id,
      provider: 'manual',
      provider_symbol: null,
      risk: meta.risk,
      quantity: 1,
      avg_price: null,
      ccy: 'USD',
      price_usd: item.value_usd,
      value_usd: item.value_usd,
      value_eur: item.value_eur,
      share_in_bucket: 1,
      share_in_portfolio: total > 0 ? item.value_usd / total : 0,
      deltas: zeroDeltas(),
      pnl: null,
    };
    entries.push({
      meta,
      bucket: {
        id: meta.bucket_id,
        name: meta.bucket_name,
        color_accent: meta.bucket_accent,
        share: total > 0 ? item.value_usd / total : 0,
        value_usd: item.value_usd,
        value_eur: item.value_eur,
        weighted_risk: meta.risk,
        deltas: zeroDeltas(),
        pnl: null,
        holdings: [row],
      },
    });
    privateUsd += item.value_usd;
  }

  const recomputedBuckets: Bucket[] = data.buckets.map((b) => ({
    ...b,
    share: total > 0 ? b.value_usd / total : 0,
    deltas: rebaseDeltasToPortfolio(b.deltas, deltaBaseByWindow),
    pnl: rebasePnlToPortfolio(b.pnl, total),
    holdings: b.holdings.map((h) => ({
      ...h,
      share_in_portfolio:
        total > 0 && h.value_usd != null ? h.value_usd / total : 0,
      deltas: rebaseDeltasToPortfolio(h.deltas, deltaBaseByWindow),
      pnl: rebasePnlToPortfolio(h.pnl, total),
    })),
  }));

  const virtualAssetClass: AssetClassRollup[] = entries.map(({ bucket, meta }) => ({
    class: meta.asset_class,
    value_usd: bucket.value_usd,
    value_eur: bucket.value_eur,
    share: total > 0 ? bucket.value_usd / total : 0,
  }));

  const virtualRiskPoints: RiskHeatmapPoint[] = entries.map(({ bucket, meta }) => ({
    symbol: bucket.name,
    bucket: bucket.id,
    value_usd: bucket.value_usd,
    risk: meta.risk,
  }));

  const geography = mergeGeography(data.geography, entries, total);
  const coveredTradableUsd = data.totals.tradable_usd * data.totals.pnl.coverage;
  const rebasedTotalsPnl = {
    ...rebasePnlToPortfolio(data.totals.pnl, total)!,
    coverage: total > 0 ? coveredTradableUsd / total : 0,
  };

  return {
    ...data,
    totals: {
      ...data.totals,
      deltas: rebasedTotalsDeltas,
      pnl: rebasedTotalsPnl,
    },
    buckets: [...recomputedBuckets, ...entries.map((e) => e.bucket)],
    asset_classes: [
      ...data.asset_classes.map((r) => ({
        ...r,
        share: total > 0 ? r.value_usd / total : 0,
      })),
      ...virtualAssetClass,
    ],
    geography,
    currency_exposure: mergeCurrency(data.currency_exposure, total, privateUsd),
    risk_heatmap: [...data.risk_heatmap, ...virtualRiskPoints],
  };
}

function portfolioBaseByWindow(
  totalUsd: number,
  deltas: Deltas
): Record<keyof Deltas, number> {
  const out = {} as Record<keyof Deltas, number>;
  for (const key of DELTA_KEYS) out[key] = totalUsd - deltas[key].abs_usd;
  return out;
}

function rebaseDeltaToPortfolio(delta: Delta, portfolioBaseUsd: number): Delta {
  return {
    ...delta,
    pct: portfolioBaseUsd > 0 ? delta.abs_usd / portfolioBaseUsd : 0,
  };
}

function rebaseDeltasToPortfolio(
  deltas: Deltas,
  baseByWindow: Record<keyof Deltas, number>
): Deltas {
  return {
    d1: rebaseDeltaToPortfolio(deltas.d1, baseByWindow.d1),
    d7: rebaseDeltaToPortfolio(deltas.d7, baseByWindow.d7),
    d30: rebaseDeltaToPortfolio(deltas.d30, baseByWindow.d30),
    d90: rebaseDeltaToPortfolio(deltas.d90, baseByWindow.d90),
    ytd: rebaseDeltaToPortfolio(deltas.ytd, baseByWindow.ytd),
    y1: rebaseDeltaToPortfolio(deltas.y1, baseByWindow.y1),
    y2: rebaseDeltaToPortfolio(deltas.y2, baseByWindow.y2),
  };
}

function rebasePnlToPortfolio(pnl: PnL | null, totalUsd: number): PnL | null {
  if (pnl == null) return null;
  return {
    ...pnl,
    pct: totalUsd > 0 ? pnl.abs_usd / totalUsd : 0,
  };
}

function mergeGeography(
  existing: GeographyRollup[],
  entries: VirtualEntry[],
  total: number
): GeographyRollup[] {
  const map = new Map<string, { usd: number; eur: number }>();
  for (const r of existing) map.set(r.region, { usd: r.value_usd, eur: r.value_eur });
  for (const { bucket, meta } of entries) {
    const prev = map.get(meta.region) ?? { usd: 0, eur: 0 };
    map.set(meta.region, {
      usd: prev.usd + bucket.value_usd,
      eur: prev.eur + bucket.value_eur,
    });
  }
  return Array.from(map.entries())
    .map(([region, v]) => ({
      region,
      value_usd: v.usd,
      value_eur: v.eur,
      share: total > 0 ? v.usd / total : 0,
    }))
    .sort((a, b) => b.value_usd - a.value_usd);
}

function mergeCurrency(
  existing: CurrencyExposure[],
  total: number,
  privateUsd: number
): CurrencyExposure[] {
  const map = new Map<string, number>();
  for (const c of existing) map.set(c.ccy, c.value_usd);
  if (privateUsd > 0) {
    map.set(
      PRIVATE_ASSETS_CCY,
      (map.get(PRIVATE_ASSETS_CCY) ?? 0) + privateUsd
    );
  }
  return Array.from(map.entries())
    .map(([ccy, value_usd]) => ({
      ccy,
      value_usd,
      share: total > 0 ? value_usd / total : 0,
    }))
    .sort((a, b) => b.value_usd - a.value_usd);
}
